/**
 * Custom hook to cleanup empty HTML content
 * OPTIMIZED: Only runs when content actually needs cleanup, not on every text change
 */
import { useEffect, useRef } from "react";
import { stripHTML } from "../utils/textUtils";
import { updateTextAndSelectedText } from "../utils/stateUpdateUtils";

export const useContentCleanup = ({ text, setTexts, setSelectedText }) => {
  const lastCleanupRef = useRef({});
  const cleanupTimeoutRef = useRef(null);

  useEffect(() => {
    if (!text) return;

    // Debounce cleanup to avoid running on every keystroke
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
    }

    cleanupTimeoutRef.current = setTimeout(() => {
      const divIds = [
        { id: `note-div-${text?.id}`, field: "note" },
        { id: `task-div-${text?.id}`, field: "task" },
        { id: `content-div-${text?.id}`, field: "content" },
      ];

      const updates = {};
      let needsUpdate = false;

      // Step 1: Cleanup top-level fields
      // Only check if field has content that needs cleanup
      divIds.forEach(({ id, field }) => {
        const el = document.getElementById(id);
        if (!el) return;

        const fieldValue = text[field];
        const strippedText = stripHTML(fieldValue);

        // Only cleanup if field has HTML but no text content
        // Skip if we already cleaned this recently
        const cleanupKey = `${text.id}-${field}`;
        const lastCleanup = lastCleanupRef.current[cleanupKey];
        const shouldCleanup =
          !strippedText && fieldValue && fieldValue.trim() !== "";

        if (shouldCleanup && lastCleanup !== fieldValue) {
          updates[field] = "";
          updates[`${field}PlainText`] = "";
          updates[`${field}IsHtml`] = false;
          updates[`${field}Underline`] = false;
          updates._lastUpdated = Date.now();
          needsUpdate = true;
          lastCleanupRef.current[cleanupKey] = fieldValue;
          el.innerHTML = "";
        }
      });

      // Step 2: Cleanup partsByType by ID
      // Only run if selectedContent is a part
      const selectedId = text?.selectedContent?.id;
      let updatedPartsByType = null;
      const changedParts = {};

      if (text?.selectedContent?.type === "part" && text?.partsByType) {
        updatedPartsByType = Object.fromEntries(
          Object.entries(text.partsByType).map(([typeName, typeData]) => {
            const updatedParts = typeData.parts.map((partObj) => {
              if (partObj.id !== selectedId) return partObj;

              const updatedPart = { ...partObj };
              let changed = false;

              // Only cleanup if content exists but is empty after stripping
              const noteStripped = stripHTML(partObj.note);
              if (partObj.note && !noteStripped) {
                updatedPart.note = "";
                updatedPart.notePlainText = "";
                updatedPart.noteIsHtml = false;
                updatedPart.noteUnderline = false;
                changed = true;
              }

              const taskStripped = stripHTML(partObj.task);
              if (partObj.task && !taskStripped) {
                updatedPart.task = "";
                updatedPart.taskPlainText = "";
                updatedPart.taskIsHtml = false;
                updatedPart.taskUnderline = false;
                changed = true;
              }

              if (changed) {
                updatedPart._lastUpdated = Date.now();
                changedParts[typeName] = true;
              }

              return changed ? updatedPart : partObj;
            });

            return [
              typeName,
              changedParts[typeName]
                ? { ...typeData, parts: updatedParts }
                : typeData,
            ];
          })
        );
      }

      // Step 3: Apply updates if needed
      if (needsUpdate || updatedPartsByType) {
        updateTextAndSelectedText({
          textId: text.id,
          setTexts,
          setSelectedText,
          updates: {
            ...(updatedPartsByType ? { partsByType: updatedPartsByType } : {}),
            ...updates,
          },
        });
      }
    }, 500); // Debounce cleanup by 500ms to avoid interfering with typing

    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [
    text?.id,
    text?.selectedContent?.id,
    text?.note,
    text?.task,
    text?.content,
    setTexts,
    setSelectedText,
  ]);
};
